/* ===== Reauthentication guard — wraps sensitive actions behind a password re-check ===== */

import { useState, type ReactNode } from 'react'
import { useApp } from '@/presentation/providers/useApp'
import { getSupabaseClient } from '@/infrastructure/supabase/app'
import { ReauthModal } from '@/presentation/components/auth/ReauthModal'

export function useReauthGuard() {
  const { user } = useApp()
  const [pendingAction, setPendingAction] = useState<(() => void | Promise<void>) | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isVerifying, setIsVerifying] = useState(false)

  const guard = (action: () => void | Promise<void>) => {
    setPendingAction(() => action)
    setError(null)
    setIsOpen(true)
  }

  const handleCancel = () => {
    setIsOpen(false)
    setPendingAction(null)
    setError(null)
  }

  const handleConfirm = async (password: string) => {
    if (!user?.email) {
      setError('No fue posible verificar la sesion actual.')
      return
    }

    setIsVerifying(true)
    setError(null)

    try {
      const client = getSupabaseClient()
      // Supabase has no separate "reauthenticate with the existing session" call - re-verifying
      // the password via signInWithPassword both confirms it and refreshes the session token.
      const { error: signInError } = await client.auth.signInWithPassword({
        email: user.email,
        password,
      })
      if (signInError) throw signInError

      setIsOpen(false)
      const action = pendingAction
      setPendingAction(null)
      if (action) {
        await action()
      }
    } catch (error) {
      console.error('Error de reautenticacion:', error)
      setError('Contrasena incorrecta. Intenta nuevamente.')
    } finally {
      setIsVerifying(false)
    }
  }

  const reauthModal: ReactNode = (
    <ReauthModal
      isOpen={isOpen}
      isVerifying={isVerifying}
      error={error}
      onCancel={handleCancel}
      onConfirm={(password) => void handleConfirm(password)}
    />
  )

  return { guard, reauthModal }
}
